import React, { useState, Fragment } from 'react';
import { toast } from 'react-toastify';
import { apiRequest } from '../../utils/api';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { useAuth } from '../auth/AuthComponents';

const subjectOptions = [
  'General Question',
  'Technical Support',
  'Billing Issue',
  'Feature Request',
  'Bug Report',
];

const ContactSupportForm = ({ initialName = '', initialEmail = '', onClose }) => {
  const { isAuthenticated } = useAuth();
  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState(initialEmail);
  const [subject, setSubject] = useState(subjectOptions[0]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiRequest('contact/submit', {
        method: 'POST',
        body: { name, email, subject, message },
      });
      toast.success('Your message has been sent!');
      setName('');
      setEmail('');
      setSubject(subjectOptions[0]);
      setMessage('');
      if (onClose) onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to send message.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="mb-2 text-left">
        {!isAuthenticated && (
          <p className="text-xs text-[var(--color-text-muted)] mb-2">For login user only, if you don't have an account use the chat bot</p>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Name</label>
          <input
            type="text"
            className="themed-input w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            placeholder="Your name"
            value={name}
            onChange={e => setName(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Email</label>
          <input
            type="email"
            className="themed-input w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            placeholder="your@email.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Subject</label>
        <Listbox value={subject} onChange={setSubject}>
          <div className="relative mt-1">
            <Listbox.Button className="themed-input relative w-full cursor-pointer rounded-lg py-2 pl-3 pr-10 text-left focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]">
              <span className="block truncate">{subject}</span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronUpDownIcon className="h-5 w-5 text-[var(--color-text-muted)]" aria-hidden="true" />
              </span>
            </Listbox.Button>
            <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
              <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg elevated-card py-1 text-base shadow-lg focus:outline-none">
                {subjectOptions.map((option, idx) => (
                  <Listbox.Option
                    key={option}
                    className={({ active, selected }) =>
                      `relative cursor-pointer select-none py-2 pl-10 pr-4 
                      ${active ? 'bg-[var(--color-primary-soft)] text-[var(--color-text)]' : 'text-[var(--color-text-secondary)]'}
                      ${selected ? 'font-semibold' : 'font-normal'}`
                    }
                    value={option}
                  >
                    {({ selected }) => (
                      <>
                        <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>{option}</span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                            <CheckIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </Listbox>
      </div>
      <div>
        <label className="block text-sm font-medium text-[var(--color-text-secondary)] mb-2">Message</label>
        <textarea
          rows={4}
          className="themed-input w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
          placeholder="Describe your question or issue..."
          value={message}
          onChange={e => setMessage(e.target.value)}
          required
        />
      </div>
      <div className="flex justify-end space-x-2">
        {onClose && (
          <button
            type="button"
            className="px-4 py-2 rounded-lg bg-[var(--color-surface-muted)] text-[var(--color-text)] font-medium hover:opacity-90 transition border border-[var(--color-border)]"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="brand-gradient-btn w-full py-3 px-4 rounded-xl font-medium"
          disabled={loading}
        >
          {loading ? 'Sending...' : 'Send Message'}
        </button>
      </div>
    </form>
  );
};

export default ContactSupportForm; 